import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { IconType } from 'react-icons';

import {
  animateTealIconHighlight,
  createStepHighlightCycle,
  killStepHighlightTweens,
  SBR_STEP_HIGHLIGHT_DURATION,
  SBR_TEAL_ICON_ON_DARK,
} from '../../lib/gsapHighlight';
import SbrIcon from './SbrIcon';

export type HiwAiFeatureItem = {
  title: string;
  body: string;
  Icon: IconType;
};

type Props = {
  items: HiwAiFeatureItem[];
  className?: string;
};

/** How It Works AI banner features — scroll stagger + one-to-one highlight cycle. */
export default function HiwAiFeaturesList({ items, className = '' }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      if (!rootRef.current) return;

      const features = rootRef.current.querySelectorAll<HTMLElement>('.hiw-ai-feature');
      const icons = rootRef.current.querySelectorAll<HTMLElement>('.hiw-ai-feature__icon');
      const titles = rootRef.current.querySelectorAll<HTMLElement>('.hiw-ai-feature__title');
      const bodies = rootRef.current.querySelectorAll<HTMLElement>('.hiw-ai-feature__body');
      if (!features.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let cycleTween: gsap.core.Timeline | null = null;
      let scrollTrigger: ScrollTrigger | undefined;

      const highlightFeature = (index: number) => {
        killStepHighlightTweens(icons, titles, bodies);

        features.forEach((feature, j) => {
          const icon = icons[j];
          const title = titles[j];
          const body = bodies[j];
          const isActive = j === index;

          if (icon) {
            animateTealIconHighlight(icon, isActive, SBR_TEAL_ICON_ON_DARK, 1.1);
          }

          if (title) {
            if (isActive) {
              gsap.to(title, {
                color: '#ffffff',
                duration: SBR_STEP_HIGHLIGHT_DURATION,
                ease: 'power2.out',
                overwrite: 'auto',
              });
            } else {
              gsap.set(title, { color: 'rgba(255, 255, 255, 0.92)' });
            }
          }

          if (body) {
            if (isActive) {
              gsap.to(body, {
                color: 'rgba(255, 255, 255, 0.78)',
                duration: SBR_STEP_HIGHLIGHT_DURATION,
                ease: 'power2.out',
                overwrite: 'auto',
              });
            } else {
              gsap.set(body, { color: 'rgba(255, 255, 255, 0.5)' });
            }
          }

          if (isActive) {
            gsap.to(feature, {
              x: 4,
              duration: SBR_STEP_HIGHLIGHT_DURATION,
              ease: 'power2.out',
              overwrite: 'auto',
            });
          } else {
            gsap.set(feature, { x: 0 });
          }
        });
      };

      const startCycle = () => {
        cycleTween?.kill();
        if (reducedMotion) {
          highlightFeature(0);
          return;
        }
        cycleTween = createStepHighlightCycle(items.length, highlightFeature);
      };

      const cleanup = () => {
        cycleTween?.kill();
        cycleTween = null;
      };

      if (reducedMotion) {
        gsap.set(features, { opacity: 1, y: 0, x: 0, clearProps: 'opacity,transform' });
        startCycle();
        return cleanup;
      }

      gsap.set(features, { opacity: 0, y: 18 });

      let revealStarted = false;
      const reveal = gsap.timeline({
        paused: true,
        onComplete: startCycle,
      });

      features.forEach((feature, i) => {
        reveal.to(
          feature,
          { opacity: 1, y: 0, duration: 0.55, ease: 'power2.out' },
          i * 0.14,
        );
      });

      const playReveal = () => {
        if (revealStarted) return;
        revealStarted = true;
        reveal.play();
      };

      scrollTrigger = ScrollTrigger.create({
        trigger: rootRef.current,
        start: 'top 82%',
        onEnter: playReveal,
        onEnterBack: playReveal,
      });

      if (ScrollTrigger.isInViewport(rootRef.current)) {
        playReveal();
      }

      return () => {
        scrollTrigger?.kill();
        reveal.kill();
        cleanup();
      };
    },
    { scope: rootRef, dependencies: [items.length] },
  );

  return (
    <div
      ref={rootRef}
      className={`hiw-ai-features d-flex flex-column gap-4${className ? ` ${className}` : ''}`.trim()}
    >
      {items.map((item) => (
        <article key={item.title} className="hiw-ai-feature d-flex gap-3 align-items-start">
          <div className="hiw-ai-feature__icon" aria-hidden>
            <SbrIcon icon={item.Icon} size={22} />
          </div>
          <div className="hiw-ai-feature__copy">
            <div className="hiw-ai-feature__title fw-semibold">{item.title}</div>
            <p className="hiw-ai-feature__body text-white-50 small mb-0 mt-1">{item.body}</p>
          </div>
        </article>
      ))}
    </div>
  );
}
